import RiotControl from 'riotcontrol'
import logger from '../logger'
import {
  forEach,
  cloneDeep,
  get,
  merge,
  set,
  noop,
  isEmpty,
  pick,
  has,
  isEqual,
} from 'lodash-es'
import extendedObservable from './base'
import orderGetters from './order-observable-getters'
import api from '../api'
import { Errors } from '../utils/error-utils'
import AjaxUtils from '../utils/ajax-utils'
import UserUtils from '../../util/utils/user-utils'
import AnalyticsStrings from '../metrics/strings'
import appProps from '../../util/app-props'


// ///////////////////////////
// Order Observable  //
// ///////////////////////////

class OrderObservable {
  constructor() {
    const defaultModel = {
      balance: null,
      billingAddress: {},
      checksum: null,
      coupons: [],
      estimatedDividendEarned: null,
      externalOrderId: null,
      lineItems: [],
      orderIssues: [],
      orderStatus: {},
      shippingChargesGroups: [],
      taxes: { total: null },
      tenders: [],
      total: null,
      totalPrice: { totalProductAdjusted: null, totalShippingChargeAdjusted: null },
      user: {},
      validation: null,
    }

    extendedObservable(this, 'orderModel', defaultModel, orderGetters)

    const resetModel = () => {
      forEach(defaultModel, (value, key) => {
        this.model[key] = cloneDeep(value)
      })
    }

    const successXHRHandler = (json, status, xhr) => {
      logger('SUCCESS :: Communicated with OP Order Service', 'info')()
      logger(json, 'info')()
      updateChecksum(xhr)
      // We need to send the membershipAutoVerify flag on the next analytics page view event if
      // the user has become verified as a member in checkout. We don't want to send it if they
      // are an already verified as a member when they enter checkout
      if (this.model.lineItems.length // this is so we don't compare to the default model on initial audit
          && !UserUtils.isMember(this.model.safeGet('customerInfo'))
          && UserUtils.isMember(json.customerInfo)) {
        this.model.membershipAutoVerify = true
      }

      // We potentially need to call audit multiple times on the review page but can only call with
      // `eadIncluded` once so if the line items have fulfillment data attached we need to extract
      // that before resetting the model and then reapply it afterward.
      let lineItems = []
      if (get(this.model, 'lineItems[0].fulfillment.fulfillmentInfos[0].ead', -1) > -1) {
        lineItems = cloneDeep(this.model.lineItems)
      }

      resetModel()

      // Set the model to the JSON response
      merge(this.model, json)

      // Reset the fulfilment methods if we need to
      if (lineItems.length) {
        this.model.lineItems.forEach((newlineItem) => {
          lineItems.forEach((oldLineItem) => {
            // If the new fulfillment data doesn't have EADs and the old data does use the old data
            const newEad = get(newlineItem, 'fulfillment.fulfillmentInfos[0].ead')
            const oldEad = get(oldLineItem, 'fulfillment.fulfillmentInfos[0].ead')
            if (newlineItem.reiRefId === oldLineItem.reiRefId
              && newEad === -1
              && oldEad > -1) {
              // eslint-disable-next-line no-param-reassign
              newlineItem.fulfillment = oldLineItem.fulfillment
            }
          })
        })
      }

      this.notifyObservers()
      return this.model
    }

    const successXHRPatchHandler = (path, value) => {
      // Set the model property to the value
      set(this.model, path.slice(1).replace('/', '.'), value)

      this.notifyObservers()
      return this.model
    }

    const updateExternalOrderId = (json) => {
      logger('SUCCESS :: Communicated with OP identifiers-request Service', 'info')()
      logger(json, 'info')()

      // Also update internal orderId and userId
      this.model.externalOrderId = json.externalOrderId
      this.model.orderId = json.orderId
      this.model.user.reiRefId = json.userId
      RiotControl.trigger('order.retrieved')

      return json
    }

    const updateOrderNotes = (json, status, xhr) => {
      this.model.notes = json
      updateChecksum(xhr)
      return json
    }

    const removeOrderNotes = (noteType) => {
      this.model.notes = this.model.notes.filter(note => note.type !== noteType)
      this.notifyObservers()
    }

    const updateChecksum = (xhr) => {
      // update the order checksum
      const checksum = xhr.getResponseHeader('checksum')
      if (checksum) {
        updateOrderChecksum(checksum)
      }
      return checksum
    }

    const errorXHRHandler = (xhr, status, errorThrown) => {
      const responseErrors = get(xhr, 'responseJSON.error')
      let xhrErrors
      if (responseErrors) {
        xhrErrors = new Errors(responseErrors)
      }

      // Make sure that the error is an actual XHR error before handling.
      // Check the first argument for a stack trace.
      if (typeof xhr !== 'undefined' && !get(xhr, 'stack')) {
        const location = xhr.getResponseHeader('Location')
        // If the order already exists
        if (xhr.status === 409 && location) {
          logger('XHR :: Order already exists in OP Service...', 'info')()
          // Need to reload model data

          const found = /([A-Za-z0-9])\w+$/.exec(location)
          const id = found[0]

          // Set the external order id of the resource
          RiotControl.trigger('updateExternalOrderId', id)

          // GET (Audit) the order
          this.trigger('auditOrder')
        } else if (xhr.status === 412 && xhrErrors && xhrErrors.hasOrderLimitError()) {
          // Order has reached maximum number of items. Alert the user.
          RiotControl.trigger('modalFactory.create', {
            title: 'Your cart is full',
            template: 'line-item-limit-modal',
            width: 400,
            data: {
              xhr,
            },
            buttons: [{
              title: 'Ok',
              el_classes: 'btn btn-primary',
            }],
          })
        } else if (xhr.status >= 400 && xhr.status < 500) {
          this.trigger('orderError')
          RiotControl.trigger('orderUpdated', this.model)
          // basic error handling for bad request (400 response)
          window.location = '/ShoppingCart' // eslint-disable-line no-undef
        } else if (xhr.status >= 500) {
          RiotControl.trigger('orderUpdated', this.model)
          RiotControl.trigger('modalFactory.create', {
            title: 'Error: Service unavailable',
            template: 'service-error-order-modal',
            width: 400,
            data: {
              xhr,
            },
            buttons: [{
              title: 'Keep shopping',
              el_classes: 'btn btn-primary',
              on_click: () => {
                window.location = '/'; // eslint-disable-line
              },
            }],
          })
        } else {
          RiotControl.trigger('orderUpdated', this.model)
          logger(`ERROR :: (XHR) Failed to communicate with OP Service ${errorThrown}`, 'error')()
          logger(`Status: ${status}`, 'error')()
          logger(xhr, 'error')()
        }
      } else {
        // unknown error
        logger(xhr, 'error')()
      }
      throw xhr
    }

    const successSubmitHandler = (json, status, xhr) => {
      logger('SUCCESS :: Submitted order to OP Service', 'info')()
      successXHRHandler(json, status, xhr)
      return this.model
    }

    const errorSubmitHandler = (xhr, status, errorThrown) => {
      logger(`ERROR :: (XHR) Failed to submit order to the OP Service ${errorThrown}`, 'error')()
      logger(`Status: ${status}`, 'error')()
      logger(xhr, 'error')()

      const errorCode = get(xhr, 'responseJSON.error["0"].code')

      if (errorCode === 30202) {
        window.location.href = '/checkout' // eslint-disable-line no-undef
      }

      // If checksum is invalid
      if (AjaxUtils.checkForBadChecksum(xhr)) {
        AjaxUtils.handleChecksumMismatch()
      }

      if (AjaxUtils.checkForNoOrder(xhr)) {
        AjaxUtils.handleNoOrder()
      }

      updateChecksum(xhr)

      return xhr
    }

    const requestOrder = () => new Promise((resolve, reject) => {
      api.order.identifiers()
        .then(updateExternalOrderId)
        .then(resolve)
        .catch(errorXHRHandler)
        .catch(reject)
    })

    const patchOrder = (data) => {
      RiotControl.trigger('updateAppModel', 'requestsInFlight', true)
      return api.order.patch(data)
        .then((json, status, xhr) => updateChecksum(xhr))
        // @todo: So we created a PATCH endpoint to update the order but Akamai doesn't allow PATCH
        // so we need to sort that out and then revist this (probably post-holiday). In the mean
        // time the enpoint is only used for updating `isGift` so I'm hard coding the path for now.
        // .then(() => successXHRPatchHandler(data[0].path, data[0].value))
        .then(() => successXHRPatchHandler('/isGift', data.isGift))
        .catch(errorXHRHandler)
        .done((res) => {
          RiotControl.trigger('updateAppModel', 'requestsInFlight', false)
          return res
        })
    }

    const addOrderNotes = notes => new Promise((resolve, reject) =>
      api.order.notes.post(notes)
        .then(updateOrderNotes)
        .then(resolve)
        .catch(errorXHRHandler)
        .catch(reject)
    )

    const deleteOrderNotes = noteType => new Promise((resolve, reject) =>
      api.order.notes.delete(noteType)
        .then((json, status, xhr) => { updateChecksum(xhr) })
        .then(resolve)
        .catch(errorXHRHandler)
        .catch(reject))

    this.auditOrder = options => new Promise((resolve, reject) => {
      const nextIsSummary = { eadIncluded: this.appModel.nextPageIsSummary() }
      const opts = Object.assign((nextIsSummary.eadIncluded) ? nextIsSummary : { eadIncluded: false }, options)

      return api.order.audit(opts)
        .then(successXHRHandler)
        .then((order) => {
          resolve()
          RiotControl.trigger('order.audited', order)
        })
        .catch(errorXHRHandler)
        .catch(reject)
    })

    const addItem = (data, cb, err = noop()) =>
      api.cart.add(data)
        .then(() => this.auditOrder())
        .then(() => {
          const useByEads = appProps.get('checkout.audit.use.by.ead', false)
          if (!useByEads) RiotControl.trigger('getShippingCharges')
        })
        .then(() => RiotControl.trigger('resetFulfillment'))
        .then(cb)
        .catch(errorXHRHandler)
        .catch(err)

    const deleteItems = async (data, cb, err = noop()) => {
      try {
        for (let i = 0, l = data.length; i < l; i += 1) {
          // The eslint flag below is saying we should be doing Promise.all so the requests are
          // in parallel instead await in a loop, however in this case they need to be sequential
          // so we specifically need to do this rather than Promise.all
          // eslint-disable-next-line no-await-in-loop
          await api.cart.delete(data[i])
        }
      } catch (e) {
        errorXHRHandler(e)
      }

      return this.auditOrder()
        .then(() => RiotControl.trigger('resetFulfillment'))
        .then(cb)
        .catch(errorXHRHandler)
        .catch(err)
    }

    const removeItemFromCart = (item, cb, err = noop()) =>
      api.cart.delete(item)
        .then(() => this.auditOrder())
        .then(() => {
          RiotControl.trigger('resetFulfillment')
          RiotControl.trigger('removeFromFulfillmentModel', item.reiRefId)
        })
        .then(() => RiotControl.trigger('fetchFulfillments', () => this.notifyObservers()))
        .then(() => (RiotControl.trigger('hideGlobalOverlay')))
        .then(cb)
        .catch(errorXHRHandler)
        .catch(err)

    /**
     * Append Accertify payload to submit payload if Accertify is enabled
     * via the common components site app prop. We are using the inverse of
     * `use.accertify` because that prop also toggles the original Accertify
     * code on the confirmation page.
     * "when confirmation page Accertify is off, turn this Accertify code on"

     * @returns {Object|null} Analytics DTO object or null
     */
    const getAnalyticsDto = () => {
      if (appProps.get('use.accertify')) return null;

      const { _bcn } = window;
      const deviceTransactionId = _bcn?.dvc?.getTID();
      const ubaId = _bcn?.getToken();
      const ubaEvents = _bcn?.getEvents();

      if (deviceTransactionId && ubaId && ubaEvents) {
        logger(`Accertify Device TID: ${deviceTransactionId}`, 'info')();
        logger(`Accertify UBA ID: ${ubaId}`, 'info')();
        logger(`Accertify UBA Events: ${ubaEvents}`, 'info')();

        return {
          eventSource: 'web',
          deviceTransactionId,
          ubaId,
          ubaEvents,
        };
      }

      return null;
    }

    const submitOrder = () => new Promise((resolve, reject) => {
      let tender = null;

      // fail fast
      const encryptionEnabled = appProps.get('chase.end-to-end-encryption.enabled', false)
      if (encryptionEnabled && !this.appModel.testCCEnabled) {
        if (get(this.tendersModel, 'encryptedStoredCreditCard') === null) {
          RiotControl.trigger('hideGlobalOverlay')
          return
        }
      } else if (get(this.tendersModel, 'storedCreditCard') === null) {
        RiotControl.trigger('hideGlobalOverlay')
        return
      }

      tender = mapTenderPayload();

      // Get analytics data
      const analyticsDto = getAnalyticsDto();

      // Payload structure: tender fields spread at root level + analyticsDto
      const tenderDto = {
        ...tender,
        ...(analyticsDto && { analyticsDto }),
      };

      const requestData = JSON.stringify(tenderDto);
      api.submit.post(requestData)
        .then(successSubmitHandler)
        .then(resolve)
        .catch(errorSubmitHandler)
        .catch(reject)
    })

    /**
     * Due to the fact there is no post back on order confirmation, the header
     * will show the user as still logged in (even though they are not). This method
     * resets the header to the appropriate user state when an order is submitted
     */
    const logoutOnSubmit = () => {
      const $headerWrapper = document.querySelector('#headerWrapper')
      const $accountLink = $headerWrapper.querySelector('#rei-account-link')
      const $signInLink = $headerWrapper.querySelector('#rei-sign-in')
      const $signOutLink = $headerWrapper.querySelector('#rei-sign-out')
      const $register = $headerWrapper.querySelector('#rei-register')
      if ($accountLink) {
        $accountLink.remove()
        $signInLink.style.setProperty('display', 'block')
        $signOutLink.parentNode.remove()
        $register.parentNode.style.setProperty('display', 'block')
      }
    }

    const mapTenderPayload = () => {
      
      // Default empty tender structure
      const emptyTender = {
        type: null,
        number: null,
        expiration: null,
        pin: null,
        creditCardType: null,
        updateSavedTender: false,
      }

      const balance = this.model.safeGet('balance')
      const data = get(this.tendersModel, 'storedCreditCard', null)
      
      if (isEmpty(data) || !balance) return emptyTender

      if (data.sequenceId && data.expiration) {
        if (data.updateSavedTender) return pick(data, ['sequenceId', 'expiration', 'updateSavedTender', 'pin', 'type'])
        return pick(data, ['sequenceId', 'pin', 'type']) // shouldn't reach here but #forsafety
      } else if (data.sequenceId) {
        return pick(data, ['sequenceId', 'pin', 'type'])
      // If ILA
      } else if (data.ilaAppId) {
        return pick(data, ['ilaAppId', 'type'])
      }
      // new card
      const encryptionEnabled = appProps.get('chase.end-to-end-encryption.enabled', false)
      if (encryptionEnabled && !this.appModel.testCCEnabled) {
        const encrypted = get(this.tendersModel, 'encryptedStoredCreditCard', null)
        if (encrypted === null) return emptyTender
        const { cardNumber, integrityCheckVal, keyId, phase, securityCode } = encrypted
        const { creditCardType, expiration, updateSavedTender } = data
        const mapped = {
          type: 'ENCRYPTED_CREDITCARD',
          integrityCheck: integrityCheckVal,
          phaseId: phase,
          keyId,
          creditCardType,
          number: cardNumber,
          pin: securityCode,
          expiration,
          updateSavedTender,
        }

        // encrypted
        return mapped
      }
      // non-encrypted
      return pick(data, ['number', 'expiration', 'pin', 'type', 'creditCardType', 'updateSavedTender'])
    }

    const updateOrderChecksum = (checksum) => {
      // `getResponseHeader` returns a string so we must parse the
      // checksum since it is an integer on the order
      const parsed = parseInt(checksum, 10)
      if (typeof parsed !== 'number' || Number.isNaN(parsed)) {
        logger('Checksum not defined, persisting existing checksum', 'warn')()
        return
      }
      logger(`Updating checksum to ${checksum}`, 'info')()
      this.model.checksum = checksum
    }

    this.on('registerOrderObserver', observer => this.registerObserver(observer))
    this.on('unregisterOrderObserver', observer => this.unregisterObserver(observer))

    this.on('requestOrder', (cb, ecb) => {
      logger('requestOrder', 'debug')()
      logger('XHR :: Requesting order in OP Service...', 'info')()

      requestOrder()
        .then(this.notifyObservers)
        .then(() => {
          cb(this.model)
        })
        .catch((err) => {
          logger(err, 'debug')()
          if (ecb && typeof ecb === 'function') ecb(err)
        })
    })

    this.on('addOrderNotes', (notes, cb) => {
      logger('addOrderNotes', 'debug')()
      logger('XHR :: Adding order notes...', 'info')()
      addOrderNotes(notes)
        .then(this.notifyObservers)
        .then(cb)
        .catch((err) => {
          logger(err, 'debug')()
        })
    })

    this.on('deleteOrderNotes', (noteType, cb) => {
      logger('deleteOrderNotes', 'debug')()
      logger('XHR :: Deleting order notes...', 'info')()
      deleteOrderNotes(noteType)
        .then(this.notifyObservers)
        .then(cb)
        .catch((err) => {
          logger(err, 'debug')()
        })
    })

    this.on('auditOrder', (cb, ecb) => {
      logger('auditOrder', 'debug')()
      logger('XHR :: Auditing order in OP Service...', 'info')()
      this.auditOrder()
        .then(cb)
        .catch((err) => {
          logger(err, 'debug')()
          if (ecb && typeof ecb === 'function') ecb(err)
        })
    })

    this.on('auditOrderWithEADs', (cb, ecb) => {
      logger('auditOrder with EADs', 'debug')()
      logger('XHR :: Auditing order with EADs in OP Service...', 'info')()
      const options = { eadIncluded: true }
      this.auditOrder(options)
        .then(cb)
        .catch((err) => {
          logger(err, 'debug')()
          if (ecb && typeof ecb === 'function') ecb(err)
        })
    })

    this.on('patchOrder', (data, cb) => {
      logger('patchOrder', 'debug')()
      logger('XHR :: Patching order...', 'info')()
      patchOrder(data)
        .then(this.notifyObservers)
        .then(cb)
        .catch((err) => {
          logger(err, 'debug')()
        })
    })

    this.on('submitOrder', (cb, errCb) => {
      logger('submitOrder', 'debug')()
      logger('XHR :: Submitting order in OP Service...', 'info')()

      RiotControl.trigger('registerTendersObserver', this)

      submitOrder()
        .then(data => new Promise((resolve) => {
          logoutOnSubmit()
          resolve(data)
        }))
        .then(this.notifyObservers)
        .then(cb)
        .catch((err) => {
          const errorContent = get(err, 'responseJSON.error')
          const orderIssuesContent = get(err, 'responseJSON.orderIssues')

          logger(err, 'debug')()

          errCb(errorContent || orderIssuesContent)
        })
    })

    this.on('submitApplePayOrder', (data, cb, errCb) => {
      logger('submitApplePayOrder', 'debug')()
      logger('XHR :: Submitting Apple Pay order in OP Service...', 'info')()

      // Get analytics data
      const analyticsDto = getAnalyticsDto();

      const tenderDto = JSON.stringify({
        paymentType: 'APPLE_PAY',
        applePayEncryptedTokenDataDto: data,
        ...(analyticsDto && { analyticsDto }),
      })

      api.submit.post(tenderDto)
        .then(successSubmitHandler)
        .then((response) => {
          logger('XHR :: submit apple pay order success. Submitted apple pay order: ->', 'info')()
          logger(response, 'info')()
          logoutOnSubmit()
          return response
        })
        .then((response) => {
          this.notifyObservers()
          return response
        })
        .then(cb)
        .catch((err) => {
          logger('XHR :: submit apple pay order failure. Failed to submit apple pay order: ->', 'error')()
          logger(err, 'error')()

          const errorContent = get(err, 'responseJSON.error')
          const orderIssuesContent = get(err, 'responseJSON.orderIssues')

          errCb(errorContent || orderIssuesContent)
        })
    })

    // Special action used to update the order from an external
    // location. Specifically, this is used with Apple Pay "Express"
    this.on('manuallySetOrderModel', (data) => {
      resetModel()
      merge(this.model, data)
      this.notifyObservers()
      RiotControl.trigger('orderModelManuallySet', this.model)
    })

    this.on('updateOrderResource', (key, data) => {
      // Check to see if the order model has
      // the key and if the data matches...
      // If the data does not match, replace it
      // with the new data
      if (has(this.model, key)) {
        if (!isEqual(this.model[key], data)) {
          logger(`STATUS :: Order update via resource -> ${key}...`, 'info')()
          this.model[key] = data
          this.notifyObservers()
        }
      }

      if (key === 'fulfillments') {
        this.fulfillmentsModel = data
        this.notifyObservers()
      }

      if (key === 'shipCharges') {
        this.shipChargesModel = restoreTrimmedAddress(this.model, data)
        this.notifyObservers()
      }

      if (key === 'savedGiftCards') {
        this.savedGiftCardsModel = data
        this.notifyObservers()
      }

      if (key === 'tenders') {
        this.tendersModel = data
        this.notifyObservers()
      }
    })

    // update the order checksum
    this.on('updateOrderChecksum', (checksum) => {
      updateOrderChecksum(checksum)
    })

    this.on('addMembership', (cb, errCb = noop()) => {
      logger('addMembership', 'debug')()
      const membershipData = {
        sku: '6485920018',
        quantity: '1',
      }
      addItem(membershipData, cb)
        .catch((err) => {
          logger(err, 'debug')
          errCb.call(this, err)
        })
    })

    this.on('removeItem', (data, cb) => {
      logger('removeItem', 'debug')()
      removeItemFromCart(data)
        .then(cb)
        .catch(err => logger(err, 'debug'))
    })

    this.on('deleteItems', (data, cb) => {
      logger('deleteItems', 'debug')()
      const itemData = data.filter(lineItem => !lineItem.couponItem)
      deleteItems(itemData)
        .then(cb)
        .catch(err => logger(err, 'debug'))
    })

    const updateGiftMessages = (payload) => {
      if (this.model.safeGet('getters.getGiftMessages').length) {
        RiotControl.trigger('deleteOrderNotes', 'GIFT_NOTE', () => {
          if (payload.giftMessages.length) {
            RiotControl.trigger('addOrderNotes', payload.giftMessages)
          } else {
            // We have to manually remove the order notes from the model here rather
            // than as part of the 'deleteOrderNotes' event in order to prevent a FOUC when
            // new order notes are added.
            removeOrderNotes('GIFT_NOTE')
          }
        })
      } else if (payload.giftMessages.length) {
        RiotControl.trigger('addOrderNotes', payload.giftMessages)
      }
    }

    this.on('displayGiftOptionsModal', (pageContext, isEdit) => {
      RiotControl.trigger('modalFactory.create', {
        title: 'Gift options',
        template: 'gift-options-modal',
        on_close: () => {
          RiotControl.trigger('analytics.sendClick', { linkName: AnalyticsStrings.link.giftOptionsClose(pageContext, isEdit) })
        },
        data: { pageContext, isEdit },
        buttons: [{
          title: 'Save',
          el_classes: 'btn btn-primary',
          on_click: (payload) => {
            RiotControl.trigger('analytics.sendClick', { linkName: AnalyticsStrings.link.giftOptionsSave(pageContext, isEdit) })
            updateGiftMessages(payload)
          },
        }, {
          title: 'Cancel',
          el_classes: 'btn btn-secondary',
          on_click: () => {
            RiotControl.trigger('analytics.sendClick', { linkName: AnalyticsStrings.link.giftOptionsCancel(pageContext, isEdit) })
          },
        }],
      })
    })


    const restoreTrimmedAddress = (model, data) => {
      if (model && model.lineItems) {
        model.lineItems.forEach((item) => {
          if (get(item, 'shippingAddress.address.storeId', false)) {
            const longAddress = get(item, 'shippingAddress.address.line1', '')
            if (longAddress.length > 30) {
              const shippingGroups = get(data, 'shippingGroups', [])
              const trimmedLongAddress = longAddress.slice(0, 29)
              shippingGroups.forEach((group) => {
                const shortAddress = get(group, 'shipTo.addressLine1', '')
                if (shortAddress === trimmedLongAddress) {
                  group.shipTo.addressLine1 = longAddress // eslint-disable-line no-param-reassign
                }
              })
            }
          }
        })
      }
      return data
    }
  }
}

export const OrderObservableInstance = new OrderObservable();
